import { type WarrantyDurationTypes, type WarrantyTypes } from 'types/enums'
import { DEFAULT_ANNUAL_INTEREST_RATE } from '#app/routes/_admin+/admin.cars+/ui/ProductPrice.tsx'
import { warrantyPriceByWarrantyType } from '#app/routes/coche.$carSlug/reservar/ui/warranty.tsx'

export function formatPrice(price: number) {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    useGrouping: true,
  }).format(price)
}

export const calculateDiscountedPrice = (
  price: number,
  discount: number | null,
) => {
  if (!price) return null
  if (!discount) return price

  const discountAmount = price * (discount / 100)
  return price - discountAmount
}

export function formatDiscountedPrice(
  price: number,
  discount: number | null,
  additionalAmount?: number,
) {
  const discountedPrice = Math.round(
    calculateDiscountedPrice(price, discount ?? 0) ?? 0,
  )
  return formatPrice(discountedPrice + (additionalAmount ?? 0))
}

export const calculateCompoundInterest = (
  initialPrice: number,
  annualInterestRate: number,
  years: number,
) => {
  if (!initialPrice || annualInterestRate == null) return null

  const interestMultiplier = 1 + annualInterestRate / 100
  let accumulatedAmount = initialPrice

  for (let year = 1; year <= years; year++) {
    accumulatedAmount = accumulatedAmount * interestMultiplier
  }

  return Math.round(accumulatedAmount * 100) / 100
}

export const openingCommission = 3.95

/**
 * Calcula el capital financiado siguiendo el flujo:
 * 1. Precio del vehículo CON financiación
 * 2. Restar la entrada del cliente → CAPITAL BASE
 * 3. Calcular la comisión de apertura: CAPITAL BASE × 0,0395
 * 4. Sumar la comisión de apertura al capital base → CAPITAL FINANCIADO INICIAL
 * 5. Si el cliente contrata más años de garantía o garantía Premium: sumar ese precio → capital_financiado_final
 * 6. Aplicar porcentaje de seguro sobre el capital financiado
 * 7. Calcular la cuota con PMT sobre el capital con seguro y luego total = cuota * meses
 */
const insuranceByMonths: Record<number, number> = {
  [36]: 4.4312,
  [48]: 6.0917,
  [60]: 7.8803,
  [72]: 9.8119,
  [84]: 11.9031,
  [96]: 14.1751,
  [108]: 16.6536,
  [120]: 19.3698,
} as const
export const calculateFinancedAmount = ({
  price,
  warrantyData,
  initialEntry,
  months,
  TIN,
  hasPackOptima,
}: {
  price: number
  warrantyData: {
    type: WarrantyTypes
    duration: WarrantyDurationTypes
  }
  initialEntry: number
  months: number
  TIN: number
  hasPackOptima: boolean
}) => {
  // Paso 1: CAPITAL BASE = Precio - Entrada
  const capitalBase = price - initialEntry

  // Paso 2: Sumar garantía (si existe) → capital_financiado_final
  const warrantyPrice = warrantyPriceByWarrantyType({
    ...warrantyData,
    hasPackOptima,
  })
  const capitalWithWarranty = capitalBase + warrantyPrice

  // Paso 3: Aplicar porcentaje de seguro sobre el capital financiado
  const insurancePercentage = insuranceByMonths[months]
  const insuranceAmount =
    (capitalWithWarranty * (insurancePercentage ?? 0)) / 100
  const capitalConSeguro = capitalWithWarranty + insuranceAmount

  // Paso 4: Calcular comisión de apertura sobre el pecio + garantia y aseguranza
  const commissionAmount = capitalConSeguro * (openingCommission / 100)
  const capitalFinanciadoFinal = capitalConSeguro + commissionAmount

  // Paso 5: Calcular la cuota con PMT sobre el capital con seguro y luego total = cuota * meses
  const quota = calculateQuotaAmount({
    financedAmount: capitalFinanciadoFinal,
    TIN,
    plazos: months,
  })

  // Paso 6: Calcular el total = cuota * meses
  const total = quota * months

  return {
    capitalToFinance: capitalFinanciadoFinal,
    quota,
    total,
    commissionAmount,
    insuranceAmount,
  }
}

// Use tinPackGo for ≥72 months; otherwise use annualInterestRate
export const getApplicableTIN = (
  hasPackOptima: boolean,
  annualInterestRate: number,
  annualInterestRatePackGo?: number | null,
): number => {
  if (hasPackOptima && annualInterestRatePackGo) {
    return annualInterestRatePackGo
  }

  // Use annualInterestRate if available and > 0
  if (annualInterestRate != null && annualInterestRate > 0) {
    return annualInterestRate
  }

  return DEFAULT_ANNUAL_INTEREST_RATE
}

/**
 * Calculates the monthly payment (quota) using the PMT formula (Excel PAGO function)
 * Formula: PMT = PV * (r * (1 + r)^n) / ((1 + r)^n - 1)
 * Where:
 * - PV = financedAmount (capital a financiar)
 * - r = TIN/12 (monthly interest rate)
 * - n = plazos (number of months)
 *
 * Equivalent to Excel: =PAGO(TIN/12; plazos; -financedAmount)
 */
const calculateQuotaAmount = ({
  financedAmount,
  TIN,
  plazos,
}: {
  financedAmount: number
  TIN: number
  plazos: number
}) => {
  if (plazos === 0 || financedAmount === 0) return 0

  // Monthly interest rate (TIN / 12)
  const monthlyRate = TIN / 100 / 12

  // If interest rate is 0, simply divide the amount by months
  if (monthlyRate === 0) {
    return financedAmount / plazos
  }

  // PMT formula: PV * (r * (1 + r)^n) / ((1 + r)^n - 1)
  const onePlusRate = 1 + monthlyRate
  const onePlusRateToN = Math.pow(onePlusRate, plazos)
  const numerator = monthlyRate * onePlusRateToN
  const denominator = onePlusRateToN - 1

  return financedAmount * (numerator / denominator)
}
